import React, { useState, useRef } from "react";
import Icon from "../../common/Icon";

interface StatusCardItem {
  key: string;
  label: string;
  value: number;
  iconClass: string;
  iconName?: string;
  highlighted?: boolean;
  hasArrow?: boolean;
  tooltip: string;
}

interface ComplianceItemStatusProps {
  data: {
    scheduled_count?: number;
    preliminary_in_progress_count?: number;
    preliminary_completed_count?: number;
    final_completed_count?: number;
    dropped_audits_count?: number;
  } | null;
  isLoading?: boolean;
  onCardClick?: (key: string) => void;
}

const ComplianceItemStatus: React.FC<ComplianceItemStatusProps> = ({
  data,
  isLoading,
  onCardClick,
}) => {
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [tooltipAlign, setTooltipAlign] = useState<"left" | "right">("left");
  const tooltipTriggerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const cards: StatusCardItem[] = [
    {
      key: "scheduled_count",
      label: "Scheduled Audits",
      value: (data && data.scheduled_count) || 0,
      iconClass: "icon-scheduled",
      iconName: "scheduledAudit",
      tooltip: "Audits that are scheduled but no progress has been started yet.",
    },
    {
      key: "preliminary_in_progress_count",
      label: "Preliminary InProgress Audits",
      value: (data && data.preliminary_in_progress_count) || 0,
      iconClass: "icon-inprogress",
      iconName: "inprogressAudit",
      tooltip: "Audits currently in progress at the vendor's place.",
    },
    {
      key: "preliminary_completed_count",
      label: "Preliminary Audit Completed",
      value: (data && data.preliminary_completed_count) || 0,
      iconClass: "icon-preliminary",
      iconName: "preliminaryAudit",
      tooltip: "Audits where the preliminary audit report has been Published.",
    },
    {
      key: "final_completed_count",
      label: "Final Audit Completed",
      value: (data && data.final_completed_count) || 0,
      iconClass: "icon-final",
      iconName: "finalAudit",
      tooltip: "Audits where the final audit report has been completed and uploaded.",
    },
    {
      key: "dropped_audits_count",
      label: "Dropped Audits",
      value: (data && data.dropped_audits_count) || 0,
      iconClass: "icon-dropped",
      iconName: "droppedAudit",
      tooltip: "Audits cancelled or dropped by Auditor.",
    },
  ];

  return (
    <div className="section-card">
      <div className="section-title">Compliance Item Status</div>
      <div className="section-subtitle">
        Real-time overview of audit schedule status
      </div>

      {isLoading ? (
        <div className="section-loading">
          <div className="spinner-border text-secondary" role="status" />
          Loading...
        </div>
      ) : (
        <div className="compliance-status-cards">
          {cards.filter((card) => card.key !== "dropped_audits_count" || card.value > 0).map((card) => (
            <div
              key={card.key}
              className={`status-card ${card.highlighted ? "highlighted" : ""}`}
              onClick={() => onCardClick && onCardClick(card.key)}
              style={{ cursor: "pointer" }}
            >
              <Icon name="cardArrow" className="arrow-icon" />
              <div className={`status-icon ${card.iconClass}`}>
                <Icon name={card.iconName!} />
              </div>
              <div className="w-100">
                <div className="status-value">{card.value}</div>
                <div className="status-label-row">
                  <span className="status-label">{card.label}</span>
                  <div
                    ref={(el) => { tooltipTriggerRefs.current[card.key] = el; }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ position: "relative" }}
                    onMouseEnter={() => {
                      const el = tooltipTriggerRefs.current[card.key];
                      if (el) {
                        const rect = el.getBoundingClientRect();
                        const spaceOnRight = window.innerWidth - rect.right;
                        setTooltipAlign(spaceOnRight >= 200 ? "left" : "right");
                      }
                      setActiveTooltip(card.key);
                    }}
                    onMouseLeave={() => setActiveTooltip(null)}
                  >
                    <Icon name="cardInfo" className="status-info" />
                    {activeTooltip === card.key && (
                      <div style={{
                        position: "absolute",
                        top: "calc(100% + 6px)",
                        ...(tooltipAlign === "left" ? { left: 0 } : { right: 0 }),
                        background: "#1E1E1E",
                        color: "#FFFFFF",
                        fontSize: "14px",
                        fontWeight: 400,
                        padding: "6px 10px",
                        borderRadius: "6px",
                        whiteSpace: "normal",
                        maxWidth: "200px",
                        width: "max-content",
                        zIndex: 100,
                        pointerEvents: "none",
                        boxShadow: "0px 2px 8px rgba(0,0,0,0.18)",
                      }}>
                        {card.tooltip}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ComplianceItemStatus;
